import { Chip } from "@/components/iris";
import { useState } from "react";
import { AvatarsTab, type OpenGroupRequest } from "./avatars-tab";
import { BlacklistTab } from "./blacklist-tab";
import { EntitiesEnvTab } from "./entities-env-tab";
import { GroupsUnifiedTab } from "./groups-unified-tab";
import { HygieneEnvTab } from "./hygiene-env-tab";
import { MembershipHealTab } from "./membership-heal-tab";
import { OrphansTab } from "./orphans-tab";

const TABS = [
  { key: "groups", label: "Groups" },
  { key: "entities", label: "Entities" },
  { key: "avatars", label: "Avatars" },
  { key: "blacklist", label: "Blacklist" },
  { key: "orphans", label: "Orphans" },
  { key: "hygiene", label: "Data Hygiene" },
  { key: "membership_heal", label: "Membership Heal" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

// IR-1705 r3: the page always opened on Groups, whose load is the heaviest on
// the page. An operator going to Avatars set it off first and then waited
// behind it — on prod 2026-10-20 the Avatars list 504'd twice while the Groups
// calls it had just triggered were still running. The page now reopens on the
// tab last used in this browser, so each visit loads only what is looked at.
export const DATA_TAB_STORAGE_KEY = "iris-ops.data.tab";

export function loadDataTab(storage: Pick<Storage, "getItem"> = localStorage): TabKey {
  try {
    const raw = storage.getItem(DATA_TAB_STORAGE_KEY);
    return TABS.some((t) => t.key === raw) ? (raw as TabKey) : "groups";
  } catch {
    return "groups";
  }
}

export function DataPage() {
  const [tab, setTabState] = useState<TabKey>(() => loadDataTab());
  const setTab = (next: TabKey) => {
    setTabState(next);
    try {
      localStorage.setItem(DATA_TAB_STORAGE_KEY, next);
    } catch {
      // Ignored on purpose — switching tabs must work without storage.
    }
  };

  // IR-1697: a failed group in an Avatars drill-in is opened by searching the
  // Groups tab for its scrapping_id. That id is the one key both environments
  // share; an entity id is each environment's own and is never sent across.
  const [groupSearch, setGroupSearch] = useState<string | null>(null);
  const openGroup = (g: OpenGroupRequest) => {
    setGroupSearch(g.scrappingId);
    setTab("groups");
  };

  // IR-1705: Data Management takes the full window width (the Avatars table
  // has 19 columns); see .iris-page--full in iris-kit.css.
  return (
    <div className="iris-page iris-page--full">
      <h1 className="iris-page__title" style={{ fontSize: 32, marginBottom: 20 }}>
        Data Management
      </h1>

      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        {TABS.map((t) => (
          <Chip
            key={t.key}
            active={tab === t.key}
            onClick={() => {
              setGroupSearch(null);
              setTab(t.key);
            }}
          >
            {t.label}
          </Chip>
        ))}
      </div>

      {tab === "groups" && (
        <GroupsUnifiedTab key={groupSearch ?? ""} initialSearch={groupSearch ?? ""} />
      )}
      {tab === "entities" && <EntitiesEnvTab />}
      {tab === "avatars" && <AvatarsTab onOpenGroup={openGroup} />}
      {tab === "blacklist" && <BlacklistTab />}
      {tab === "orphans" && <OrphansTab />}
      {tab === "hygiene" && <HygieneEnvTab />}
      {tab === "membership_heal" && <MembershipHealTab />}
    </div>
  );
}
